import React, { useState, useMemo, useEffect, useCallback } from "react";
import { C, canEditModule } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  DataTable, EmptyState, useToast, exportToCSV, ImportModal
} from "./erp-shared";
import { MonthlyReportButton } from "./erp-monthly-report";
import {
  getInstituteStudents,
  getInstituteCourses,
  getInstituteExams,
  getInstituteAttendance,
  getInstituteCertificates,
  getInstituteStaff,
  createInstituteStudent,
  createInstituteExam,
  createInstituteCertificate,
  createInstituteStaffMember,
  updateInstituteCourse,
  updateInstituteAttendance,
} from "./supabaseClient";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid
} from "recharts";
import {
  GraduationCap, Users, BookOpen, ClipboardList,
  Award, UserCog, BarChart3, PieChart as PieChartIcon,
  Plus, Search, AlertTriangle, ChevronDown, ChevronLeft,
  TrendingUp, CheckCircle, XCircle, Clock, FileText,
  UserPlus, Edit, Download, Upload, Link as LinkIcon, Paperclip,
  Layers, Target, Calendar
} from "lucide-react";

/* ═════════════════════════════════════════════
   ✦ Institute Page — معهد الثمر الداني للمهتدين
   ═════════════════════════════════════════════ */

const TODAY_REF = new Date("2026-06-19");

const LEVELS = [
  { key: "L0", label: "تمهيدي",  color: "#0891b2" },
  { key: "L1", label: "أول",     color: C.teal },
  { key: "L2", label: "ثاني",    color: "#7c3aed" },
  { key: "L3", label: "متقدم",   color: C.accent },
];

const ACADEMIC_STATUSES = [
  { key: "new",        label: "مستجد",  color: "#0891b2" },
  { key: "regular",    label: "منتظم",  color: C.success },
  { key: "struggling", label: "متعثر",  color: C.warning },
  { key: "absent",     label: "منقطع",  color: C.danger },
  { key: "graduated",  label: "متخرج",  color: C.accent },
];

const GENDERS = ["ذكر", "أنثى"];
const NATIONALITIES = ["أمريكا", "فرنسا", "الهند", "إندونيسيا", "نيجيريا", "كندا", "إسبانيا", "تركيا", "بنغلاديش", "باكستان", "بريطانيا", "ألمانيا"];
const LANGUAGES = ["العربية", "الإنجليزية", "الفرنسية", "الإسبانية", "الأردية", "الهندية", "الإندونيسية", "التركية", "البنغالية"];

const EXAM_TYPES = ["تمهيدي", "دوري", "نهائي"];
const EXAM_STATUSES = [
  { key: "pass",     label: "ناجح",  color: C.success },
  { key: "fail",     label: "راسب",  color: C.danger },
  { key: "deferred", label: "مؤجل",  color: C.warning },
];

const ATTENDANCE_VALUES = [
  { key: "present", label: "حضر",   color: C.success },
  { key: "absent",  label: "غاب",   color: C.danger },
  { key: "late",    label: "متأخر", color: C.warning },
  { key: "excused", label: "معذور", color: C.textLight },
];

const STAFF_ROLES = ["مشرف", "معلم"];

/* ─────────────────────────────────────────────
   ✦ Seed Data — مرشحون من المسلمين الجدد للمحاكاة
   ───────────────────────────────────────────── */
const REFERRAL_POOL = [];
/* ─── محوّلات الصفوف: snake_case (Supabase) → camelCase (الواجهة) ─── */
const mapStudent = r => ({
  id: r.id,
  name: r.name || "",
  nationality: r.nationality || "",
  language: r.language || "",
  gender: r.gender || "",
  phone: r.phone || "",
  joinDate: r.join_date || "",
  referredFromNewMuslimId: r.referred_from_new_muslim_id || "",
  currentLevel: r.current_level || "L0",
  academicStatus: r.academic_status || "new",
  supervisorId: r.supervisor_id || "",
  supervisorName: r.supervisor_name || "",
  notes: r.notes || "",
});

const mapCourse = r => ({
  id: r.id,
  levelKey: r.level_key || "",
  title: r.title || "",
  units: r.units ?? 0,
  lessonsCount: r.lessons_count ?? 0,
  link: r.link || "",
  attachments: r.attachments ?? 0,
  testsCount: r.tests_count ?? 0,
  defaultCompletionRate: r.default_completion_rate ?? 0,
});

const mapExam = r => ({
  id: r.id,
  studentId: r.student_id || "",
  studentName: r.student_name || "",
  courseId: r.course_id || "",
  courseTitle: r.course_title || "",
  type: r.type || "",
  date: r.exam_date || "",
  score: r.score ?? 0,
  maxScore: r.max_score ?? 0,
  percent: r.percent ?? 0,
  status: r.status || "pass",
  notes: r.notes || "",
});

const mapAttendance = r => ({
  id: r.id,
  studentId: r.student_id || "",
  studentName: r.student_name || "",
  sessions: Array.isArray(r.sessions) ? r.sessions : [],
});

const mapCertificate = r => ({
  id: r.id,
  studentId: r.student_id || "",
  studentName: r.student_name || "",
  type: r.type || "",
  refId: r.ref_id || "",
  refLabel: r.ref_label || "",
  issueDate: r.issue_date || "",
  certNumber: r.cert_number || "",
  delivered: !!r.delivered,
  pdfRef: r.pdf_ref || "",
});

const mapStaff = r => ({
  id: r.id,
  name: r.name || "",
  role: r.role || "",
  specialty: r.specialty || "",
  phone: r.phone || "",
  email: r.email || "",
  languages: Array.isArray(r.languages) ? r.languages : [],
  status: r.status || "",
});

const NEW_STUDENTS_BY_MONTH = [];
/* ═════════════════════════════════════════════
   ✦ Main Page Component
   ═════════════════════════════════════════════ */
export default function InstitutePage({ user, perms }) {
  const [tab, setTab] = useState("overview");
  const canEdit = perms ? canEditModule(perms, "institute") : true;

  const [students, setStudents]         = useState([]);
  const [courses, setCourses]           = useState([]);
  const [exams, setExams]               = useState([]);
  const [attendance, setAttendance]     = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [staff, setStaff]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [loadError, setLoadError]       = useState(null);

  // تحميل كل بيانات المعهد من Supabase — لا بيانات وهمية
  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [st, co, ex, at, ce, sf] = await Promise.all([
        getInstituteStudents(),
        getInstituteCourses(),
        getInstituteExams(),
        getInstituteAttendance(),
        getInstituteCertificates(),
        getInstituteStaff(),
      ]);
      const rows = r => (Array.isArray(r) ? r : r?.data || []);
      setStudents(rows(st).map(mapStudent));
      setCourses(rows(co).map(mapCourse));
      setExams(rows(ex).map(mapExam));
      setAttendance(rows(at).map(mapAttendance));
      setCertificates(rows(ce).map(mapCertificate));
      setStaff(rows(sf).map(mapStaff));
    } catch (e) {
      setLoadError("تعذّر تحميل بيانات المعهد");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const tabs = [
    { key: "overview",       label: "لوحة التحكم" },
    { key: "students",       label: "الطلاب" },
    { key: "curriculum",     label: "المستويات والمقررات" },
    { key: "studentProfile", label: "صفحة الطالب" },
    { key: "exams",          label: "الاختبارات" },
    { key: "attendance",     label: "الحضور والمتابعة" },
    { key: "certificates",   label: "الشهادات" },
    { key: "staff",          label: "المشرفون والمعلمون" },
    { key: "reports",        label: "التقارير" },
  ];

  const reportSources = [
    {
      label: "الطلاب", mode: "monthly", dateField: "joinDate", rows: students,
      columns: [
        { key: "name", label: "الاسم" },
        { key: "nationality", label: "الجنسية" },
        { key: "language", label: "اللغة" },
        { key: "joinDate", label: "تاريخ الانضمام" },
        { key: "currentLevel", label: "المستوى" },
        { key: "academicStatus", label: "الحالة الدراسية" },
      ],
    },
    {
      label: "الاختبارات", mode: "monthly", dateField: "date", rows: exams,
      columns: [
        { key: "studentName", label: "الطالب" },
        { key: "courseTitle", label: "المقرر" },
        { key: "type", label: "النوع" },
        { key: "date", label: "التاريخ" },
        { key: "percent", label: "النسبة" },
        { key: "status", label: "النتيجة" },
      ],
    },
    {
      label: "الشهادات", mode: "monthly", dateField: "issueDate", rows: certificates,
      columns: [
        { key: "studentName", label: "الطالب" },
        { key: "type", label: "النوع" },
        { key: "refLabel", label: "المرجع" },
        { key: "issueDate", label: "تاريخ الإصدار" },
        { key: "certNumber", label: "رقم الشهادة" },
      ],
    },
    { label: "المقررات", mode: "cumulative", rows: courses },
    { label: "الطاقم التعليمي", mode: "cumulative", rows: staff },
  ];

  return (
    <div>
      <ModuleHeader
        icon={GraduationCap}
        title="معهد الثمر الداني للمهتدين"
        subtitle="تعليم وتأهيل المسلمين الجدد عبر مستويات ومقررات منظمة، وإصدار شهادات معتمدة"
      >
        <MonthlyReportButton moduleTitle="معهد الثمر الداني للمهتدين" sources={reportSources} />
      </ModuleHeader>
      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {loadError && (
        <Card style={{ padding: 12, marginTop: 16, background: `${C.danger}10`, border: `1px solid ${C.danger}40`, color: C.danger, fontSize: 13 }}>
          {loadError}
        </Card>
      )}

      {loading ? (
        <Card style={{ padding: 32, marginTop: 16, textAlign: "center", color: C.textLight, fontSize: 14 }}>
          جارٍ تحميل بيانات المعهد...
        </Card>
      ) : (
      <>
      {tab === "overview"       && <OverviewTab students={students} courses={courses} certificates={certificates} staff={staff} />}
      {tab === "students"       && <StudentsTab canEdit={canEdit} students={students} setStudents={setStudents} staff={staff} />}
      {tab === "curriculum"     && <CurriculumTab canEdit={canEdit} courses={courses} setCourses={setCourses} />}
      {tab === "studentProfile" && <StudentProfileTab canEdit={canEdit} students={students} courses={courses} exams={exams} attendance={attendance} certificates={certificates} />}
      {tab === "exams"          && <ExamsTab canEdit={canEdit} students={students} courses={courses} exams={exams} setExams={setExams} />}
      {tab === "attendance"     && <AttendanceTab canEdit={canEdit} attendance={attendance} setAttendance={setAttendance} />}
      {tab === "certificates"   && <CertificatesTab canEdit={canEdit} certificates={certificates} setCertificates={setCertificates} students={students} courses={courses} />}
      {tab === "staff"          && <StaffTab canEdit={canEdit} staff={staff} setStaff={setStaff} students={students} />}
      {tab === "reports"        && <ReportsTab students={students} exams={exams} certificates={certificates} staff={staff} courses={courses} />}
      </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 1) Overview Tab — لوحة التحكم
   ───────────────────────────────────────────── */
function OverviewTab({ students, courses, certificates, staff }) {
  const total       = students.length;
  const regular     = students.filter(s => s.academicStatus === "regular").length;
  const newcomers   = students.filter(s => s.academicStatus === "new").length;
  const struggling  = students.filter(s => s.academicStatus === "struggling").length;
  const absent      = students.filter(s => s.academicStatus === "absent").length;
  const graduated   = students.filter(s => s.academicStatus === "graduated").length;
  const teachers    = staff.length;
  const activeCourses = courses.length;
  const certIssued    = certificates.length;
  const completionRate = total > 0 ? Math.round(((regular + graduated) / total) * 100) : 0;

  const statusData = ACADEMIC_STATUSES.map(s => ({
    name:  s.label,
    value: students.filter(st => st.academicStatus === s.key).length,
    color: s.color,
  })).filter(d => d.value > 0);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="إجمالي الطلاب"     value={total}          icon={Users}        color={C.primary} />
        <KPICard title="المنتظمون"          value={regular}        icon={CheckCircle}  color={C.success} />
        <KPICard title="المستجدون"          value={newcomers}      icon={UserPlus}     color="#0891b2" />
        <KPICard title="المتعثرون"          value={struggling}     icon={AlertTriangle} color={C.warning} />
        <KPICard title="المنقطعون"          value={absent}         icon={XCircle}      color={C.danger} />
        <KPICard title="المتخرجون"          value={graduated}      icon={Award}        color={C.accent} />
        <KPICard title="المعلمون"           value={teachers}       icon={UserCog}      color="#7c3aed" />
        <KPICard title="المقررات النشطة"    value={activeCourses}  icon={BookOpen}     color={C.teal} />
        <KPICard title="الشهادات الصادرة"   value={certIssued}     icon={Award}        color={C.accent} />
        <KPICard title="معدل الإكمال %"     value={`${completionRate}%`} icon={TrendingUp} color={C.success} />
      </div>

      <div style={styles.chartsRow}>
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={styles.chartTitleWrap}>
              <PieChartIcon size={18} color="#7c3aed" />
              <span style={styles.chartTitle}>توزيع الطلاب حسب الحالة الدراسية</span>
            </div>
          </div>
          {statusData.length === 0 ? (
            <EmptyState title="لا توجد بيانات للعرض" />
          ) : (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={3}>
                    {statusData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                    formatter={(v) => [`${v} طالب`]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div style={styles.legendGrid}>
                {statusData.map((d, i) => (
                  <div key={i} style={styles.legendItem}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                    <span style={{ fontSize: 11, color: C.textLight }}>{d.name}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: C.primary }}>{d.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>

        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={styles.chartTitleWrap}>
              <BarChart3 size={18} color={C.teal} />
              <span style={styles.chartTitle}>الطلاب الجدد شهرياً (12 شهر)</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={NEW_STUDENTS_BY_MONTH}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="month" fontSize={11} tick={{ fill: C.textLight }} />
              <YAxis fontSize={11} tick={{ fill: C.textLight }} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                formatter={(v) => [`${v} طالب`]}
              />
              <Bar dataKey="value" name="الطلاب الجدد" radius={[6, 6, 0, 0]} fill={C.teal} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 2) Students Tab — الطلاب
   ───────────────────────────────────────────── */
const INITIAL_STUDENT = {
  name: "", nationality: "أمريكا", language: "الإنجليزية", gender: "ذكر",
  phone: "", joinDate: "", referredFromNewMuslimId: null,
  currentLevel: "L0", academicStatus: "new", supervisorId: "", notes: "",
};

function StudentsTab({ canEdit, students, setStudents, staff }) {
  const [search, setSearch]             = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAdd, setShowAdd]           = useState(false);
  const [showRefer, setShowRefer]       = useState(false);
  const [showImport, setShowImport]     = useState(false);
  const [form, setForm]                 = useState(INITIAL_STUDENT);
  const [showToast, ToastView]          = useToast();

  const supervisors = useMemo(() => staff.filter(s => s.role === "مشرف"), [staff]);

  async function handleSave() {
    if (!form.name || !form.phone) {
      showToast("الاسم والجوال مطلوبان", "error");
      return;
    }
    const sup = staff.find(s => s.id === form.supervisorId);
    const joinDate = form.joinDate || TODAY_REF.toISOString().slice(0, 10);
    const payload = {
      name: form.name,
      nationality: form.nationality,
      language: form.language,
      gender: form.gender,
      phone: form.phone,
      join_date: joinDate,
      referred_from_new_muslim_id: form.referredFromNewMuslimId || null,
      current_level: form.currentLevel,
      academic_status: form.academicStatus,
      supervisor_id: form.supervisorId || null,
      supervisor_name: sup ? sup.name : "",
      notes: form.notes,
    };
    const { data, error } = await createInstituteStudent(payload);
    if (error) { showToast(error, "error"); return; }
    if (data) {
      setStudents(prev => [...prev, {
        id: data.id,
        ...form,
        joinDate,
        supervisorName: sup ? sup.name : "",
      }]);
    }
    setForm(INITIAL_STUDENT);
    setShowAdd(false);
    showToast("تمت إضافة الطالب بنجاح");
  }

  const STUDENT_CSV_COLUMNS = [
    { key: "name",           header: "الاسم" },
    { key: "nationality",    header: "الجنسية" },
    { key: "language",       header: "اللغة" },
    { key: "gender",         header: "الجنس" },
    { key: "phone",          header: "الجوال" },
    { key: "joinDate",       header: "تاريخ الانضمام" },
    { key: "currentLevel",   header: "المستوى" },
    { key: "academicStatus", header: "الحالة الدراسية" },
    { key: "supervisorName", header: "المشرف" },
    { key: "notes",          header: "ملاحظات" },
  ];

  function handleExport() {
    exportToCSV(filtered, STUDENT_CSV_COLUMNS, "طلاب_المعهد");
    showToast(`تم تصدير ${filtered.length} طالب`);
  }

  // الاستيراد يحفظ صفًا صفًا في Supabase ويتوقف عند أول خطأ فعلي
  async function handleImport(rows) {
    let ok = 0;
    const added = [];
    for (const r of rows) {
      if (!r.name) continue;
      const payload = {
        name: r.name,
        nationality: r.nationality || "",
        language: r.language || "",
        gender: r.gender || "",
        phone: r.phone || "",
        join_date: r.joinDate || TODAY_REF.toISOString().slice(0, 10),
        referred_from_new_muslim_id: null,
        current_level: r.currentLevel || "L0",
        academic_status: r.academicStatus || "new",
        supervisor_id: null,
        supervisor_name: r.supervisorName || "",
        notes: r.notes || "",
      };
      const { data, error } = await createInstituteStudent(payload);
      if (error) {
        showToast(`توقف الاستيراد عند "${r.name}": ${error}`, "error");
        break;
      }
      ok++;
      if (data) added.push(mapStudent({ ...payload, id: data.id }));
    }
    if (added.length) setStudents(prev => [...prev, ...added]);
    setShowImport(false);
    if (ok) showToast(`تم استيراد ${ok} طالب`);
  }

  function handleReferralPick(item) {
    setForm({
      ...INITIAL_STUDENT,
      name: item.name,
      nationality: item.nationality,
      language: item.language,
      referredFromNewMuslimId: item.id,
      joinDate: TODAY_REF.toISOString().slice(0, 10),
    });
    setShowRefer(false);
    setShowAdd(true);
  }

  const filtered = students.filter(s => {
    if (search && !s.name.includes(search) && !(s.phone || "").includes(search)) return false;
    if (filterStatus !== "all" && s.academicStatus !== filterStatus) return false;
    return true;
  });

  const columns = [
    { key: "name",           label: "الاسم",          nowrap: true },
    { key: "nationality",    label: "الجنسية",        nowrap: true },
    { key: "language",       label: "اللغة",          nowrap: true },
    { key: "gender",         label: "النوع",          nowrap: true },
    { key: "phone",          label: "الجوال",         nowrap: true },
    { key: "joinDate",       label: "تاريخ الانضمام",  nowrap: true },
    { key: "currentLevel",   label: "المستوى",        nowrap: true, render: (v) => {
        const l = LEVELS.find(x => x.key === v);
        return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${l?.color}18`, color: l?.color }}>{l?.label}</span>;
    } },
    { key: "academicStatus", label: "الحالة",        nowrap: true, render: (v) => {
        const s = ACADEMIC_STATUSES.find(x => x.key === v);
        return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${s?.color}18`, color: s?.color }}>{s?.label}</span>;
    } },
    { key: "supervisorName", label: "المشرف",         nowrap: true },
    { key: "referredFromNewMuslimId", label: "محال من", nowrap: true, render: (v) => v ? <span style={{ fontFamily: "monospace", fontSize: 11, color: C.teal }}>{v}</span> : <span style={{ color: C.textLight }}>—</span> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.toolbar}>
        <div style={styles.searchBox}>
          <Search size={14} color={C.textLight} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="بحث بالاسم أو الجوال..."
            style={styles.searchInput}
          />
        </div>
        <select style={styles.filterSelect} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">جميع الحالات</option>
          {ACADEMIC_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <Btn variant="ghost" icon={Download} onClick={handleExport}>تصدير</Btn>
        {canEdit && (
          <>
            <Btn variant="ghost" icon={Upload} onClick={() => setShowImport(true)}>استيراد</Btn>
            <Btn variant="ghost" icon={UserPlus} onClick={() => setShowRefer(true)}>إحالة من سجل المهتدين</Btn>
            <Btn icon={Plus} onClick={() => { setForm(INITIAL_STUDENT); setShowAdd(true); }}>إضافة طالب</Btn>
          </>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <DataTable columns={columns} data={filtered} />
      </div>

      {showImport && (
        <ImportModal
          title="استيراد الطلاب"
          entityLabel="طالب"
          columns={STUDENT_CSV_COLUMNS}
          onImport={handleImport}
          onClose={() => setShowImport(false)}
        />
      )}

      {showAdd && (
        <Modal title={form.referredFromNewMuslimId ? "تسجيل طالب من سجل المهتدين" : "إضافة طالب جديد"} onClose={() => { setShowAdd(false); setForm(INITIAL_STUDENT); }} width={700}>
          <div style={styles.formCol}>
            {form.referredFromNewMuslimId && (
              <div style={styles.infoBanner}>
                <UserPlus size={16} color={C.teal} />
                <span style={{ fontSize: 13, color: C.primary }}>مُحال من سجل المسلمين الجدد — رقم: <strong>{form.referredFromNewMuslimId}</strong></span>
              </div>
            )}
            <Field label="الاسم" required value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={styles.grid3}>
              <Field label="الجنسية" type="select" options={NATIONALITIES} value={form.nationality} onChange={v => setForm({ ...form, nationality: v })} />
              <Field label="اللغة" type="select" options={LANGUAGES} value={form.language} onChange={v => setForm({ ...form, language: v })} />
              <Field label="النوع" type="select" options={GENDERS} value={form.gender} onChange={v => setForm({ ...form, gender: v })} />
            </div>
            <div style={styles.grid2}>
              <Field label="الجوال" required value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
              <Field label="تاريخ الانضمام" type="date" value={form.joinDate} onChange={v => setForm({ ...form, joinDate: v })} />
            </div>
            <div style={styles.grid3}>
              <Field label="المستوى" type="select" options={LEVELS.map(l => ({ value: l.key, label: l.label }))} value={form.currentLevel} onChange={v => setForm({ ...form, currentLevel: v })} />
              <Field label="الحالة الدراسية" type="select" options={ACADEMIC_STATUSES.map(s => ({ value: s.key, label: s.label }))} value={form.academicStatus} onChange={v => setForm({ ...form, academicStatus: v })} />
              <Field label="المشرف" type="select" options={[{ value: "", label: "— اختر —" }, ...supervisors.map(s => ({ value: s.id, label: s.name }))]} value={form.supervisorId} onChange={v => setForm({ ...form, supervisorId: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" rows={2} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={styles.formActions}>
              <Btn icon={Plus} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_STUDENT); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}

      {showRefer && (
        <Modal title="إحالة من سجل المسلمين الجدد" onClose={() => setShowRefer(false)} width={640}>
          <div style={styles.formCol}>
            <p style={{ margin: 0, fontSize: 13, color: C.textLight }}>اختر مهتدياً من القائمة لإنشاء سجل طالب يربط به تلقائياً:</p>
            <div style={styles.referList}>
              {REFERRAL_POOL.map(item => {
                const alreadyEnrolled = students.some(s => s.referredFromNewMuslimId === item.id);
                return (
                  <div key={item.id} style={styles.referRow}>
                    <div>
                      <p style={{ margin: 0, fontWeight: 600, color: C.primary, fontSize: 14 }}>{item.name}</p>
                      <p style={{ margin: "4px 0 0", fontSize: 12, color: C.textLight }}>{item.nationality} — {item.language} — معرف: <span style={{ fontFamily: "monospace" }}>{item.id}</span></p>
                    </div>
                    {alreadyEnrolled ? (
                      <span style={{ padding: "4px 10px", borderRadius: 8, background: `${C.success}15`, color: C.success, fontSize: 11, fontWeight: 600 }}>مسجل بالفعل</span>
                    ) : (
                      <Btn size="sm" icon={Plus} onClick={() => handleReferralPick(item)}>تسجيل</Btn>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={styles.formActions}>
              <Btn variant="ghost" onClick={() => setShowRefer(false)}>إغلاق</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 3) Curriculum Tab — المستويات والمقررات
   ───────────────────────────────────────────── */
function CurriculumTab({ canEdit, courses, setCourses }) {
  const [openLevel, setOpenLevel] = useState("L0");
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState({ title: "", units: 0, lessonsCount: 0, link: "", attachments: 0, testsCount: 0, defaultCompletionRate: 0 });
  const [showToast, ToastView]    = useToast();

  function openEdit(course) {
    setEditing(course);
    setForm({
      title: course.title,
      units: course.units,
      lessonsCount: course.lessonsCount,
      link: course.link,
      attachments: course.attachments,
      testsCount: course.testsCount,
      defaultCompletionRate: course.defaultCompletionRate,
    });
  }

  async function handleSaveCourse() {
    const patch = {
      title: form.title,
      units: Number(form.units) || 0,
      lessons_count: Number(form.lessonsCount) || 0,
      link: form.link,
      attachments: Number(form.attachments) || 0,
      tests_count: Number(form.testsCount) || 0,
      default_completion_rate: Number(form.defaultCompletionRate) || 0,
    };
    const { data, error } = await updateInstituteCourse(editing.id, patch);
    if (error) { showToast(error, "error"); return; }
    if (data) {
      setCourses(prev => prev.map(c => c.id === editing.id ? {
        ...c,
        title: form.title,
        units: Number(form.units) || 0,
        lessonsCount: Number(form.lessonsCount) || 0,
        link: form.link,
        attachments: Number(form.attachments) || 0,
        testsCount: Number(form.testsCount) || 0,
        defaultCompletionRate: Number(form.defaultCompletionRate) || 0,
      } : c));
    }
    showToast(`تم حفظ تعديلات المقرر: ${form.title}`);
    setEditing(null);
  }

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {LEVELS.map(level => {
          const levelCourses = courses.filter(c => c.levelKey === level.key);
          const isOpen = openLevel === level.key;
          return (
            <Card key={level.key} style={{ padding: 0, overflow: "hidden" }}>
              <button
                onClick={() => setOpenLevel(isOpen ? null : level.key)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  width: "100%", padding: "16px 20px", border: "none", background: `${level.color}10`,
                  cursor: "pointer", fontFamily: "'Readex Pro', sans-serif",
                  borderRight: `4px solid ${level.color}`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Layers size={20} color={level.color} />
                  <span style={{ fontSize: 16, fontWeight: 700, color: C.primary }}>المستوى {level.label}</span>
                  <span style={{ fontSize: 12, color: C.textLight }}>({levelCourses.length} مقررات)</span>
                </div>
                {isOpen ? <ChevronDown size={20} color={level.color} /> : <ChevronLeft size={20} color={level.color} />}
              </button>
              {isOpen && (
                <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                  {levelCourses.map(course => (
                    <div key={course.id} style={styles.courseRow}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontWeight: 700, color: C.primary, fontSize: 14 }}>{course.title}</p>
                        <div style={styles.courseMetaRow}>
                          <span style={styles.courseMetaItem}><BookOpen size={12} /> {course.units} وحدة</span>
                          <span style={styles.courseMetaItem}><FileText size={12} /> {course.lessonsCount} درس</span>
                          <span style={styles.courseMetaItem}><Paperclip size={12} /> {course.attachments} مرفق</span>
                          <span style={styles.courseMetaItem}><ClipboardList size={12} /> {course.testsCount} اختبار</span>
                          <span style={styles.courseMetaItem}><Target size={12} /> {course.defaultCompletionRate}%</span>
                          {course.link && (
                            <a href={course.link} target="_blank" rel="noopener noreferrer" style={styles.courseLink}>
                              <LinkIcon size={12} /> الرابط
                            </a>
                          )}
                        </div>
                      </div>
                      {canEdit && (
                        <button onClick={() => openEdit(course)} style={styles.editBtn}>
                          <Edit size={12} /> تعديل
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {editing && (
        <Modal title={`تعديل المقرر — ${editing.title}`} onClose={() => setEditing(null)} width={620}>
          <div style={styles.formCol}>
            <Field label="عنوان المقرر" value={form.title} onChange={v => setForm({ ...form, title: v })} />
            <div style={styles.grid3}>
              <Field label="عدد الوحدات" type="number" value={form.units} onChange={v => setForm({ ...form, units: Number(v) || 0 })} />
              <Field label="عدد الدروس" type="number" value={form.lessonsCount} onChange={v => setForm({ ...form, lessonsCount: Number(v) || 0 })} />
              <Field label="عدد الاختبارات" type="number" value={form.testsCount} onChange={v => setForm({ ...form, testsCount: Number(v) || 0 })} />
            </div>
            <div style={styles.grid2}>
              <Field label="عدد المرفقات" type="number" value={form.attachments} onChange={v => setForm({ ...form, attachments: Number(v) || 0 })} />
              <Field label="معدل الإكمال الافتراضي %" type="number" min={0} max={100} value={form.defaultCompletionRate} onChange={v => setForm({ ...form, defaultCompletionRate: Number(v) || 0 })} />
            </div>
            <Field label="رابط المقرر" value={form.link} onChange={v => setForm({ ...form, link: v })} />
            <div style={styles.formActions}>
              <Btn icon={Edit} onClick={handleSaveCourse}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => setEditing(null)}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 4) Student Profile Tab — صفحة الطالب
   ───────────────────────────────────────────── */
function StudentProfileTab({ canEdit, students, courses, exams, attendance, certificates }) {
  const [selectedId, setSelectedId] = useState(students[0]?.id || "");
  const [updateOpen, setUpdateOpen] = useState(false);
  const [updateForm, setUpdateForm] = useState({ courseId: "", progress: 0 });
  const [showToast, ToastView]      = useToast();

  const student = students.find(s => s.id === selectedId);

  const levelCourses    = useMemo(() => student ? courses.filter(c => c.levelKey === student.currentLevel) : [], [student, courses]);
  const studentExams    = useMemo(() => exams.filter(e => e.studentId === selectedId), [exams, selectedId]);
  const studentAttend   = useMemo(() => attendance.find(a => a.studentId === selectedId), [attendance, selectedId]);
  const studentCerts    = useMemo(() => certificates.filter(c => c.studentId === selectedId), [certificates, selectedId]);

  if (!student) {
    return <EmptyState title="لا يوجد طلاب لعرضهم" />;
  }

  const levelInfo  = LEVELS.find(l => l.key === student.currentLevel);
  const statusInfo = ACADEMIC_STATUSES.find(s => s.key === student.academicStatus);

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: C.textLight }}>اختر طالباً:</span>
          <select style={{ ...styles.filterSelect, minWidth: 260 }} value={selectedId} onChange={e => setSelectedId(e.target.value)}>
            {students.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </Card>

      <Card style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={styles.avatarBig}>{student.name.charAt(0)}</div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <h3 style={{ margin: 0, color: C.primary, fontSize: 20, fontWeight: 700 }}>{student.name}</h3>
            <p style={{ margin: "4px 0 0", color: C.textLight, fontSize: 13 }}>{student.nationality} — {student.language} — {student.gender}</p>
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: `${levelInfo?.color}18`, color: levelInfo?.color }}>المستوى: {levelInfo?.label}</span>
              <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: `${statusInfo?.color}18`, color: statusInfo?.color }}>{statusInfo?.label}</span>
              <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: `${C.teal}15`, color: C.teal }}>المشرف: {student.supervisorName || "—"}</span>
            </div>
          </div>
          <div>
            <p style={{ margin: 0, fontSize: 11, color: C.textLight }}>الجوال</p>
            <p style={{ margin: "4px 0 0", fontSize: 14, fontWeight: 700, color: C.primary, fontFamily: "monospace" }}>{student.phone}</p>
            <p style={{ margin: "10px 0 0", fontSize: 11, color: C.textLight }}>تاريخ الانضمام</p>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: C.primary }}>{student.joinDate}</p>
          </div>
        </div>
        {student.notes && (
          <div style={{ marginTop: 14, padding: 12, background: C.bg, borderRadius: 10, fontSize: 13, color: C.text }}>
            <strong>ملاحظات:</strong> {student.notes}
          </div>
        )}
      </Card>

      <div style={styles.sectionTitleRow}>
        <span style={styles.sectionDivider}>تقدم المقررات — المستوى {levelInfo?.label}</span>
        {canEdit && <Btn size="sm" icon={Edit} onClick={() => { setUpdateForm({ courseId: levelCourses[0]?.id || "", progress: 0 }); setUpdateOpen(true); }}>تحديث التقدم في مقرر</Btn>}
      </div>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        {levelCourses.length === 0 ? (
          <EmptyState title="لا توجد مقررات في هذا المستوى" />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {levelCourses.map(course => {
              const pct = course.defaultCompletionRate;
              return (
                <div key={course.id}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: C.primary }}>{course.title}</span>
                    <span style={{ fontSize: 12, color: C.textLight }}>{pct}%</span>
                  </div>
                  <div style={styles.progressBar}>
                    <div style={{ ...styles.progressFill, width: `${pct}%`, background: pct >= 80 ? C.success : pct >= 50 ? C.teal : C.warning }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div style={styles.sectionTitleRow}>
        <span style={styles.sectionDivider}>سجل الاختبارات</span>
      </div>
      <Card style={{ padding: 0, marginBottom: 16 }}>
        {studentExams.length === 0 ? (
          <div style={{ padding: 16 }}><EmptyState title="لا توجد اختبارات" /></div>
        ) : (
          <DataTable
            columns={[
              { key: "courseTitle", label: "المقرر" },
              { key: "type",        label: "النوع",   nowrap: true },
              { key: "date",        label: "التاريخ", nowrap: true },
              { key: "score",       label: "الدرجة",  nowrap: true, render: (v, r) => `${v} / ${r.maxScore}` },
              { key: "percent",     label: "النسبة",  nowrap: true, render: (v) => `${v}%` },
              { key: "status",      label: "النتيجة", nowrap: true, render: (v) => {
                const s = EXAM_STATUSES.find(x => x.key === v);
                return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${s?.color}18`, color: s?.color }}>{s?.label}</span>;
              } },
            ]}
            data={studentExams}
          />
        )}
      </Card>

      <div style={styles.sectionTitleRow}>
        <span style={styles.sectionDivider}>سجل الحضور (آخر أسبوع)</span>
      </div>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        {studentAttend ? (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {studentAttend.sessions.map((val, i) => {
              const v = ATTENDANCE_VALUES.find(x => x.key === val);
              return (
                <div key={i} style={{ padding: "8px 14px", borderRadius: 10, background: `${v?.color}18`, color: v?.color, fontSize: 13, fontWeight: 600 }}>
                  الجلسة {i + 1}: {v?.label}
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState title="لا يوجد سجل حضور" />
        )}
      </Card>

      <div style={styles.sectionTitleRow}>
        <span style={styles.sectionDivider}>الشهادات الصادرة</span>
      </div>
      <Card style={{ padding: 0 }}>
        {studentCerts.length === 0 ? (
          <div style={{ padding: 16 }}><EmptyState title="لا توجد شهادات" /></div>
        ) : (
          <DataTable
            columns={[
              { key: "type",       label: "النوع",       nowrap: true },
              { key: "refLabel",   label: "المرجع" },
              { key: "issueDate",  label: "تاريخ الإصدار", nowrap: true },
              { key: "certNumber", label: "رقم الشهادة",   nowrap: true, render: (v) => <span style={{ fontFamily: "monospace", fontSize: 12, color: C.primary }}>{v}</span> },
              { key: "delivered",  label: "مسلّمة",      nowrap: true, render: (v) => v ? <span style={{ color: C.success, fontWeight: 700 }}>نعم</span> : <span style={{ color: C.warning, fontWeight: 700 }}>لا</span> },
            ]}
            data={studentCerts}
          />
        )}
      </Card>

      {updateOpen && (
        <Modal title="تحديث التقدم في مقرر" onClose={() => setUpdateOpen(false)} width={520}>
          <div style={styles.formCol}>
            <Field label="المقرر" type="select" options={levelCourses.map(c => ({ value: c.id, label: c.title }))} value={updateForm.courseId} onChange={v => setUpdateForm({ ...updateForm, courseId: v })} />
            <Field label="نسبة التقدم %" type="number" min={0} max={100} value={updateForm.progress} onChange={v => setUpdateForm({ ...updateForm, progress: Number(v) || 0 })} />
            <div style={styles.formActions}>
              <Btn icon={CheckCircle} onClick={() => { showToast("تم تحديث التقدم بنجاح"); setUpdateOpen(false); }}>تحديث</Btn>
              <Btn variant="ghost" onClick={() => setUpdateOpen(false)}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 5) Exams Tab — الاختبارات
   ───────────────────────────────────────────── */
const INITIAL_EXAM = {
  studentId: "", courseId: "", type: "دوري",
  date: "", score: 0, maxScore: 20, status: "pass", notes: "",
};

function ExamsTab({ canEdit, students, courses, exams, setExams }) {
  const [filterType, setFilterType]     = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showAdd, setShowAdd]           = useState(false);
  const [form, setForm]                 = useState(INITIAL_EXAM);
  const [showToast, ToastView]          = useToast();

  async function handleSave() {
    if (!form.studentId || !form.courseId) {
      showToast("الطالب والمقرر مطلوبان", "error");
      return;
    }
    const student = students.find(s => s.id === form.studentId);
    const course  = courses.find(c => c.id === form.courseId);
    const pct = form.maxScore > 0 ? Math.round((Number(form.score) / Number(form.maxScore)) * 100) : 0;
    const examDate = form.date || TODAY_REF.toISOString().slice(0, 10);
    const payload = {
      student_id: form.studentId,
      student_name: student ? student.name : "",
      course_id: form.courseId,
      course_title: course ? course.title : "",
      type: form.type,
      exam_date: examDate,
      score: Number(form.score) || 0,
      max_score: Number(form.maxScore) || 0,
      percent: pct,
      status: form.status,
      notes: form.notes,
    };
    const { data, error } = await createInstituteExam(payload);
    if (error) { showToast(error, "error"); return; }
    if (data) {
      setExams(prev => [...prev, {
        id: data.id,
        studentId: form.studentId,
        studentName: student ? student.name : "",
        courseId: form.courseId,
        courseTitle: course ? course.title : "",
        type: form.type,
        date: examDate,
        score: Number(form.score) || 0,
        maxScore: Number(form.maxScore) || 0,
        percent: pct,
        status: form.status,
        notes: form.notes,
      }]);
    }
    setForm(INITIAL_EXAM);
    setShowAdd(false);
    showToast("تمت إضافة الاختبار بنجاح");
  }

  const filtered = exams.filter(e => {
    if (filterType !== "all" && e.type !== filterType) return false;
    if (filterStatus !== "all" && e.status !== filterStatus) return false;
    return true;
  });

  const avgScore   = exams.length > 0 ? Math.round(exams.reduce((a, e) => a + (e.percent || 0), 0) / exams.length) : 0;
  const passRate   = exams.length > 0 ? Math.round((exams.filter(e => e.status === "pass").length / exams.length) * 100) : 0;
  const deferredCt = exams.filter(e => e.status === "deferred").length;

  const columns = [
    { key: "studentName", label: "الطالب",  nowrap: true },
    { key: "courseTitle", label: "المقرر" },
    { key: "type",        label: "النوع",   nowrap: true },
    { key: "date",        label: "التاريخ", nowrap: true },
    { key: "score",       label: "الدرجة",  nowrap: true, render: (v, r) => `${v} / ${r.maxScore}` },
    { key: "percent",     label: "النسبة",  nowrap: true, render: (v) => `${v}%` },
    { key: "status",      label: "النتيجة", nowrap: true, render: (v) => {
      const s = EXAM_STATUSES.find(x => x.key === v);
      return <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${s?.color}18`, color: s?.color }}>{s?.label}</span>;
    } },
    { key: "notes",       label: "ملاحظات" },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.toolbar}>
        <select style={styles.filterSelect} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="all">جميع الأنواع</option>
          {EXAM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select style={styles.filterSelect} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">جميع النتائج</option>
          {EXAM_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        {canEdit && <Btn icon={Plus} onClick={() => { setForm(INITIAL_EXAM); setShowAdd(true); }}>إضافة اختبار</Btn>}
      </div>

      <div style={{ marginTop: 16 }}>
        <DataTable columns={columns} data={filtered} />
      </div>

      <div style={{ ...styles.kpiRow, marginTop: 24 }}>
        <KPICard title="متوسط الدرجات" value={`${avgScore}%`} icon={BarChart3}  color={C.teal} />
        <KPICard title="معدل النجاح"   value={`${passRate}%`} icon={CheckCircle} color={C.success} />
        <KPICard title="الاختبارات المؤجلة" value={deferredCt} icon={Clock}     color={C.warning} />
      </div>

      {showAdd && (
        <Modal title="إضافة اختبار جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_EXAM); }} width={680}>
          <div style={styles.formCol}>
            <div style={styles.grid2}>
              <Field label="الطالب" required type="select" options={[{ value: "", label: "— اختر —" }, ...students.map(s => ({ value: s.id, label: s.name }))]} value={form.studentId} onChange={v => setForm({ ...form, studentId: v })} />
              <Field label="المقرر" required type="select" options={[{ value: "", label: "— اختر —" }, ...courses.map(c => ({ value: c.id, label: c.title }))]} value={form.courseId} onChange={v => setForm({ ...form, courseId: v })} />
            </div>
            <div style={styles.grid3}>
              <Field label="نوع الاختبار" type="select" options={EXAM_TYPES} value={form.type} onChange={v => setForm({ ...form, type: v })} />
              <Field label="التاريخ" type="date" value={form.date} onChange={v => setForm({ ...form, date: v })} />
              <Field label="النتيجة" type="select" options={EXAM_STATUSES.map(s => ({ value: s.key, label: s.label }))} value={form.status} onChange={v => setForm({ ...form, status: v })} />
            </div>
            <div style={styles.grid2}>
              <Field label="الدرجة" type="number" value={form.score} onChange={v => setForm({ ...form, score: v })} />
              <Field label="الدرجة الكاملة" type="number" value={form.maxScore} onChange={v => setForm({ ...form, maxScore: v })} />
            </div>
            <Field label="ملاحظات" type="textarea" rows={2} value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
            <div style={styles.formActions}>
              <Btn icon={Plus} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_EXAM); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 6) Attendance Tab — الحضور والمتابعة
   ───────────────────────────────────────────── */
function AttendanceTab({ canEdit, attendance, setAttendance }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState({ sessions: ["present", "present", "present", "present"] });
  const [showToast, ToastView] = useToast();

  function openEdit(row) {
    setEditing(row);
    setForm({ sessions: [...row.sessions] });
  }

  async function handleSave() {
    const patch = { sessions: [...form.sessions] };
    const { data, error } = await updateInstituteAttendance(editing.id, patch);
    if (error) { showToast(error, "error"); return; }
    if (data) {
      setAttendance(prev => prev.map(a => a.id === editing.id ? { ...a, sessions: [...form.sessions] } : a));
    }
    setEditing(null);
    showToast("تم تحديث الحضور بنجاح");
  }

  const alerts = useMemo(() => attendance.filter(a => {
    let maxStreak = 0, current = 0;
    a.sessions.forEach(v => {
      if (v === "absent") { current++; maxStreak = Math.max(maxStreak, current); }
      else { current = 0; }
    });
    return maxStreak >= 3;
  }), [attendance]);

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}

      <Card style={{ padding: 16, marginBottom: 16, borderRight: `4px solid ${alerts.length > 0 ? C.danger : C.success}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: alerts.length > 0 ? 12 : 0 }}>
          <AlertTriangle size={18} color={alerts.length > 0 ? C.danger : C.success} />
          <span style={{ fontWeight: 700, color: C.primary, fontSize: 14 }}>
            تنبيهات الانقطاع — {alerts.length > 0 ? `${alerts.length} طلاب غابوا 3 جلسات متتالية أو أكثر` : "لا توجد تنبيهات"}
          </span>
        </div>
        {alerts.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {alerts.map(a => (
              <span key={a.id} style={{ padding: "6px 12px", borderRadius: 8, background: `${C.danger}12`, color: C.danger, fontSize: 12, fontWeight: 600 }}>
                {a.studentName}
              </span>
            ))}
          </div>
        )}
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={styles.thCell}>الطالب</th>
                {[1, 2, 3, 4].map(i => (
                  <th key={i} style={styles.thCell}>الجلسة {i}</th>
                ))}
                {canEdit && <th style={styles.thCell}>إجراء</th>}
              </tr>
            </thead>
            <tbody>
              {attendance.map((row, i) => (
                <tr key={row.id}>
                  <td style={{ ...styles.tdCell, fontWeight: 600, color: C.primary, borderBottom: i === attendance.length - 1 ? "none" : `1px solid ${C.borderLight}` }}>
                    {row.studentName}
                  </td>
                  {row.sessions.map((val, j) => {
                    const v = ATTENDANCE_VALUES.find(x => x.key === val);
                    return (
                      <td key={j} style={{ ...styles.tdCell, borderBottom: i === attendance.length - 1 ? "none" : `1px solid ${C.borderLight}` }}>
                        <span style={{ display: "inline-block", padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${v?.color}18`, color: v?.color }}>
                          {v?.label}
                        </span>
                      </td>
                    );
                  })}
                  {canEdit && (
                    <td style={{ ...styles.tdCell, borderBottom: i === attendance.length - 1 ? "none" : `1px solid ${C.borderLight}` }}>
                      <button onClick={() => openEdit(row)} style={styles.editBtn}>
                        <Edit size={12} /> تحديث
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {editing && (
        <Modal title={`تحديث الحضور — ${editing.studentName}`} onClose={() => setEditing(null)} width={520}>
          <div style={styles.formCol}>
            {[0, 1, 2, 3].map(i => (
              <Field
                key={i}
                label={`الجلسة ${i + 1}`}
                type="select"
                options={ATTENDANCE_VALUES.map(v => ({ value: v.key, label: v.label }))}
                value={form.sessions[i]}
                onChange={v => {
                  const next = [...form.sessions];
                  next[i] = v;
                  setForm({ sessions: next });
                }}
              />
            ))}
            <div style={styles.formActions}>
              <Btn icon={CheckCircle} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => setEditing(null)}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 7) Certificates Tab — الشهادات
   ───────────────────────────────────────────── */
function CertificatesTab({ canEdit, certificates, setCertificates, students, courses }) {
  const [showToast, ToastView] = useToast();

  const readyToIssue = useMemo(() => {
    // Simulate: count courses with defaultCompletionRate = 100 (zero in seed)
    // Plus suggest from any course with rate >= 90 not yet certified
    const suggestions = [];
    courses.forEach(course => {
      if (course.defaultCompletionRate >= 90) {
        students.forEach(student => {
          if (student.currentLevel === course.levelKey) {
            const exists = certificates.some(c => c.studentId === student.id && c.refId === course.id);
            if (!exists) suggestions.push({ studentId: student.id, studentName: student.name, courseId: course.id, courseTitle: course.title });
          }
        });
      }
    });
    return suggestions.slice(0, 5);
  }, [students, courses, certificates]);

  async function handleAutoIssue() {
    if (readyToIssue.length === 0) return;
    const issueDate = TODAY_REF.toISOString().slice(0, 10);
    const created = [];
    for (let i = 0; i < readyToIssue.length; i++) {
      const r = readyToIssue[i];
      const certNumber = `CERT-AUTO-${Date.now().toString().slice(-6)}-${i}`;
      const payload = {
        student_id: r.studentId,
        student_name: r.studentName,
        type: "إتمام مقرر",
        ref_id: r.courseId,
        ref_label: r.courseTitle,
        issue_date: issueDate,
        cert_number: certNumber,
        delivered: false,
        pdf_ref: "",
      };
      const { data, error } = await createInstituteCertificate(payload);
      if (error) { showToast(error, "error"); return; }
      if (data) {
        created.push({
          id: data.id,
          studentId: r.studentId,
          studentName: r.studentName,
          type: "إتمام مقرر",
          refId: r.courseId,
          refLabel: r.courseTitle,
          issueDate,
          certNumber,
          delivered: false,
          pdfRef: "",
        });
      }
    }
    if (created.length) setCertificates(prev => [...prev, ...created]);
    showToast(`تم اقتراح إصدار ${created.length} شهادات`);
  }

  const columns = [
    { key: "studentName", label: "الطالب",          nowrap: true },
    { key: "type",        label: "النوع",            nowrap: true, render: (v) => <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${C.teal}18`, color: C.teal }}>{v}</span> },
    { key: "refLabel",    label: "المرجع" },
    { key: "issueDate",   label: "تاريخ الإصدار",     nowrap: true },
    { key: "certNumber",  label: "رقم الشهادة",       nowrap: true, render: (v) => <span style={{ fontFamily: "monospace", fontSize: 12, color: C.primary }}>{v}</span> },
    { key: "delivered",   label: "مسلّمة",          nowrap: true, render: (v) => v ? <span style={{ color: C.success, fontWeight: 700 }}>نعم</span> : <span style={{ color: C.warning, fontWeight: 700 }}>لا</span> },
    { key: "pdfRef",      label: "ملف PDF",          nowrap: true, render: (v) => v ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: C.teal, fontSize: 12 }}><Download size={12} />{v}</span> : <span style={{ color: C.textLight }}>—</span> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}

      <Card style={{ padding: 16, marginBottom: 16, background: `${C.accent}08`, borderRight: `4px solid ${C.accent}` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Award size={20} color={C.accent} />
            <div>
              <p style={{ margin: 0, fontWeight: 700, color: C.primary, fontSize: 14 }}>
                {readyToIssue.length} شهادات جاهزة للإصدار
              </p>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: C.textLight }}>
                اقتراح تلقائي للطلاب الذين بلغ معدل إكمالهم 90% أو أكثر في مقررات مستواهم
              </p>
            </div>
          </div>
          {canEdit && readyToIssue.length > 0 && (
            <Btn icon={CheckCircle} onClick={handleAutoIssue}>مراجعة واقتراح الإصدار</Btn>
          )}
        </div>
      </Card>

      <DataTable columns={columns} data={certificates} />
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 8) Staff Tab — المشرفون والمعلمون
   ───────────────────────────────────────────── */
const INITIAL_STAFF = {
  name: "", role: "معلم", specialty: "", phone: "", email: "",
  languages: "", status: "active",
};

function StaffTab({ canEdit, staff, setStaff, students }) {
  const [search, setSearch]    = useState("");
  const [showAdd, setShowAdd]  = useState(false);
  const [form, setForm]        = useState(INITIAL_STAFF);
  const [showToast, ToastView] = useToast();

  async function handleSave() {
    if (!form.name || !form.specialty) {
      showToast("الاسم والتخصص مطلوبان", "error");
      return;
    }
    const languages = form.languages ? form.languages.split(/،|,|\n/).map(x => x.trim()).filter(Boolean) : [];
    const payload = {
      name: form.name,
      role: form.role,
      specialty: form.specialty,
      phone: form.phone,
      email: form.email,
      languages,
      status: form.status,
    };
    const { data, error } = await createInstituteStaffMember(payload);
    if (error) { showToast(error, "error"); return; }
    if (data) {
      setStaff(prev => [...prev, {
        id: data.id,
        name: form.name,
        role: form.role,
        specialty: form.specialty,
        phone: form.phone,
        email: form.email,
        languages,
        status: form.status,
      }]);
    }
    setForm(INITIAL_STAFF);
    setShowAdd(false);
    showToast("تمت إضافة العضو بنجاح");
  }

  const filtered = staff.filter(s => {
    if (search && !s.name.includes(search) && !s.specialty.includes(search)) return false;
    return true;
  });

  const columns = [
    { key: "name",      label: "الاسم",     nowrap: true },
    { key: "role",      label: "الدور",     nowrap: true, render: (v) => <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: v === "مشرف" ? `${C.primary}18` : `${C.teal}18`, color: v === "مشرف" ? C.primary : C.teal }}>{v}</span> },
    { key: "specialty", label: "التخصص" },
    { key: "phone",     label: "الجوال",    nowrap: true },
    { key: "email",     label: "البريد",    nowrap: true },
    { key: "languages", label: "اللغات",    render: (v) => (v || []).join("، ") },
    { key: "assignedStudents", label: "طلاب مُسندون", nowrap: true, render: (_, r) => students.filter(s => s.supervisorId === r.id).length },
    { key: "status",    label: "الحالة",    nowrap: true, render: (v) => <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: v === "active" ? `${C.success}18` : `${C.textLight}18`, color: v === "active" ? C.success : C.textLight }}>{v === "active" ? "نشط" : "غير نشط"}</span> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.toolbar}>
        <div style={styles.searchBox}>
          <Search size={14} color={C.textLight} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="بحث بالاسم أو التخصص..."
            style={styles.searchInput}
          />
        </div>
        {canEdit && <Btn icon={Plus} onClick={() => { setForm(INITIAL_STAFF); setShowAdd(true); }}>إضافة عضو</Btn>}
      </div>

      <div style={{ marginTop: 16 }}>
        <DataTable columns={columns} data={filtered} />
      </div>

      {showAdd && (
        <Modal title="إضافة معلم/مشرف جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_STAFF); }} width={640}>
          <div style={styles.formCol}>
            <Field label="الاسم" required value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={styles.grid2}>
              <Field label="الدور" type="select" options={STAFF_ROLES} value={form.role} onChange={v => setForm({ ...form, role: v })} />
              <Field label="التخصص" required value={form.specialty} onChange={v => setForm({ ...form, specialty: v })} />
            </div>
            <div style={styles.grid2}>
              <Field label="الجوال" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
              <Field label="البريد الإلكتروني" value={form.email} onChange={v => setForm({ ...form, email: v })} />
            </div>
            <Field label="اللغات (مفصولة بفواصل)" placeholder="العربية، الإنجليزية..." value={form.languages} onChange={v => setForm({ ...form, languages: v })} />
            <Field label="الحالة" type="select" options={[{ value: "active", label: "نشط" }, { value: "inactive", label: "غير نشط" }]} value={form.status} onChange={v => setForm({ ...form, status: v })} />
            <div style={styles.formActions}>
              <Btn icon={Plus} onClick={handleSave}>حفظ</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_STAFF); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ 9) Reports Tab — التقارير
   ───────────────────────────────────────────── */
function ReportsTab({ students, exams, certificates, staff, courses }) {
  const [showToast, ToastView] = useToast();

  const monthlyEnrollments = students.filter(s => (s.joinDate || "").startsWith("2026-06")).length;
  const monthlyExams       = exams.filter(e => (e.date || "").startsWith("2026-06")).length;
  const teachersCount      = staff.filter(s => s.role === "معلم").length;
  const supervisorsCount   = staff.filter(s => s.role === "مشرف").length;
  const deliveredCerts     = certificates.filter(c => c.delivered).length;
  const pendingCerts       = certificates.filter(c => !c.delivered).length;
  const passRate           = exams.length > 0 ? Math.round((exams.filter(e => e.status === "pass").length / exams.length) * 100) : 0;

  const reports = [
    {
      key: "monthly",
      title: "التقرير الشهري",
      icon: Calendar,
      color: C.teal,
      lines: [
        `الطلاب المسجلون هذا الشهر: ${monthlyEnrollments}`,
        `الاختبارات المُجراة: ${monthlyExams}`,
        `الشهادات الجاهزة: ${pendingCerts}`,
      ],
    },
    {
      key: "level",
      title: "تقرير المستويات",
      icon: Layers,
      color: "#7c3aed",
      lines: LEVELS.map(l => `${l.label}: ${students.filter(s => s.currentLevel === l.key).length} طالب — ${courses.filter(c => c.levelKey === l.key).length} مقرر`),
    },
    {
      key: "teacher",
      title: "تقرير المعلمين",
      icon: UserCog,
      color: C.primary,
      lines: [
        `إجمالي المعلمين: ${teachersCount}`,
        `إجمالي المشرفين: ${supervisorsCount}`,
        `متوسط الطلاب لكل مشرف: ${supervisorsCount > 0 ? Math.round(students.length / supervisorsCount) : 0}`,
      ],
    },
    {
      key: "certificates",
      title: "تقرير الشهادات",
      icon: Award,
      color: C.accent,
      lines: [
        `إجمالي الشهادات: ${certificates.length}`,
        `الشهادات المُسلَّمة: ${deliveredCerts}`,
        `قيد التسليم: ${pendingCerts}`,
        `معدل النجاح في الاختبارات: ${passRate}%`,
      ],
    },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={styles.reportsGrid}>
        {reports.map(r => {
          const Icon = r.icon;
          return (
            <Card key={r.key} style={{ padding: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: `${r.color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon size={20} color={r.color} />
                </div>
                <span style={{ fontSize: 15, fontWeight: 700, color: C.primary }}>{r.title}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                {r.lines.map((line, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.text }}>
                    <CheckCircle size={12} color={r.color} />
                    {line}
                  </div>
                ))}
              </div>
              <Btn size="sm" icon={Download} onClick={() => showToast(`جاري توليد ${r.title}...`)}>توليد التقرير</Btn>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Styles
   ───────────────────────────────────────────── */
const styles = {
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: 20,
    marginBottom: 16,
  },
  toolbar: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: C.bg,
    borderRadius: 8,
    padding: "6px 12px",
    border: `1px solid ${C.border}`,
    flex: 1,
    maxWidth: 320,
  },
  searchInput: {
    border: "none",
    background: "transparent",
    outline: "none",
    fontSize: 13,
    fontFamily: "'Readex Pro', sans-serif",
    color: C.primary,
    width: "100%",
  },
  filterSelect: {
    padding: "7px 12px",
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    fontSize: 13,
    fontFamily: "'Readex Pro', sans-serif",
    color: C.primary,
    background: C.white,
    cursor: "pointer",
  },
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
    gap: 20,
    marginBottom: 20,
  },
  chartCard: { padding: 20 },
  chartHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  chartTitleWrap: { display: "flex", alignItems: "center", gap: 8 },
  chartTitle: { fontSize: 15, fontWeight: 700, color: C.primary },
  legendGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    justifyContent: "center",
    marginTop: 8,
  },
  legendItem: { display: "flex", alignItems: "center", gap: 4 },
  sectionTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 8,
    marginBottom: 8,
    flexWrap: "wrap",
  },
  sectionDivider: {
    paddingBottom: 4,
    borderBottom: `2px solid ${C.teal}30`,
    fontSize: 13,
    fontWeight: 700,
    color: C.teal,
  },
  formCol: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
  },
  grid3: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 12,
  },
  formActions: {
    display: "flex",
    gap: 8,
    marginTop: 4,
  },
  infoBanner: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    background: `${C.teal}10`,
    borderRadius: 10,
  },
  referList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    maxHeight: 360,
    overflowY: "auto",
  },
  referRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: 12,
    background: C.bg,
    borderRadius: 10,
    border: `1px solid ${C.borderLight}`,
  },
  courseRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: 12,
    background: C.bg,
    borderRadius: 10,
    border: `1px solid ${C.borderLight}`,
  },
  courseMetaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 6,
  },
  courseMetaItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    color: C.textLight,
  },
  courseLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    color: C.teal,
    textDecoration: "none",
    fontWeight: 600,
  },
  editBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "5px 10px",
    borderRadius: 8,
    background: `${C.teal}12`,
    color: C.teal,
    border: "none",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "'Readex Pro', sans-serif",
  },
  avatarBig: {
    width: 64,
    height: 64,
    borderRadius: 16,
    background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`,
    color: C.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 26,
    flexShrink: 0,
  },
  progressBar: {
    width: "100%",
    height: 8,
    borderRadius: 6,
    background: C.borderLight,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    borderRadius: 6,
    transition: "width 0.3s",
  },
  thCell: {
    padding: "14px 16px",
    fontSize: 13,
    fontWeight: 700,
    color: C.textLight,
    borderBottom: `1px solid ${C.border}`,
    background: "#fafafa",
    whiteSpace: "nowrap",
    textAlign: "right",
  },
  tdCell: {
    padding: "12px 16px",
    fontSize: 13,
    color: C.text,
    whiteSpace: "nowrap",
    textAlign: "right",
  },
  reportsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 16,
  },
};
